import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { PaymentLogo, LABELS } from '../components/common/PaymentIcon';
import api from '../utils/api';

const STEPS = ['Select Game', 'Your Info', 'Select Payment', 'Upload Receipt'];

export default function Deposit() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [platforms, setPlatforms] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [myDeposits, setMyDeposits] = useState([]);
  const [form, setForm] = useState({ platform: '', gameUsername: '', fullName: '', amount: '', paymentMethod: '' });
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [screenshot, setScreenshot] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedId, setSubmittedId] = useState(null);
  const [notifying, setNotifying] = useState(false);
  const [notified, setNotified] = useState(false);
  const [error, setError] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    api.get('/platforms').then(({ data }) => setPlatforms(data.platforms || []));
    api.get('/payments').then(({ data }) => setPaymentMethods(data.methods || []));
    if (user) api.get('/deposits/my').then(({ data }) => setMyDeposits(data.deposits || []));
  }, [user]);

  const handleScreenshot = (e) => {
    const file = e.target.files[0]; if (!file) return;
    setScreenshot(file); setScreenshotPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async () => {
    if (!screenshot) { setError('Please upload a payment screenshot'); return; }
    setSubmitting(true); setError('');
    try {
      const fd = new FormData();
      fd.append('platform', form.platform);
      fd.append('gameUsername', form.gameUsername);
      fd.append('fullName', form.fullName);
      fd.append('amount', form.amount);
      fd.append('paymentMethod', form.paymentMethod);
      fd.append('screenshot', screenshot);
      const { data } = await api.post('/deposits', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSubmittedId(data.deposit._id);
      setSubmitted(true);
    } catch (err) { setError(err.response?.data?.message || 'Error submitting deposit'); }
    finally { setSubmitting(false); }
  };

  const handleNotifyAdmin = async () => {
    if (!submittedId) return;
    setNotifying(true);
    try {
      await api.post('/deposits/notify-admin', { depositId: submittedId });
      setNotified(true);
    } catch {}
    finally { setNotifying(false); }
  };

  const canNext = () => {
    if (step === 0) return form.platform;
    if (step === 1) return form.gameUsername && form.fullName && form.amount > 0;
    if (step === 2) return form.paymentMethod;
    return true;
  };

  if (!user) return (
    <div style={{ paddingTop: 70, minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 56, marginBottom: 16 }}>💰</div>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 24, marginBottom: 16 }}>Login Required</h2>
        <Link to="/login" className="btn btn-primary">Login Now</Link>
      </div>
    </div>
  );

  if (submitted) return (
    <div style={{ paddingTop: 70, minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="card" style={{ maxWidth: 520, width: '100%', textAlign: 'center', padding: '48px 32px' }}>
        <div style={{ fontSize: 64, marginBottom: 16 }}>✅</div>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 26, color: '#10b981', marginBottom: 12 }}>Deposit Submitted!</h2>
        <p style={{ color: 'var(--text-secondary)', fontSize: 15, lineHeight: 1.7, marginBottom: 20 }}>
          Your payment screenshot has been received. Admin will verify and load your account within <strong style={{ color: '#f5c400' }}>1–2 minutes</strong>.
        </p>

        {/* Notify admin button */}
        {!notified ? (
          <div style={{ background: 'rgba(245,196,0,0.08)', border: '1px solid rgba(245,196,0,0.25)', borderRadius: 12, padding: '16px 20px', marginBottom: 20 }}>
            <p style={{ color: 'var(--text-primary)', fontWeight: 600, marginBottom: 12, fontSize: 15 }}>📨 Want to notify admin right now?</p>
            <button onClick={handleNotifyAdmin} disabled={notifying} style={{ background: 'linear-gradient(135deg,#0088cc,#006aaa)', border: 'none', borderRadius: 10, padding: '11px 24px', color: '#fff', fontWeight: 700, fontSize: 14, cursor: notifying ? 'not-allowed' : 'pointer', fontFamily: 'var(--font-body)', width: '100%' }}>
              {notifying ? '⟳ Sending...' : '💬 Send to Admin / Manager via Chat'}
            </button>
          </div>
        ) : (
          <div style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: 12, padding: '14px 20px', marginBottom: 20 }}>
            <p style={{ color: '#10b981', fontWeight: 600, fontSize: 15 }}>✅ Admin & Manager have been notified via Live Chat!</p>
          </div>
        )}

        <div style={{ display: 'flex', gap: 12 }}>
          <button className="btn btn-primary" style={{ flex: 1 }} onClick={() => navigate('/chat')}>💬 Open Chat</button>
          <button className="btn btn-secondary" style={{ flex: 1 }} onClick={() => { setSubmitted(false); setStep(0); setForm({ platform:'',gameUsername:'',fullName:'',amount:'',paymentMethod:'' }); setScreenshot(null); setScreenshotPreview(null); setSubmittedId(null); setNotified(false); }}>New Deposit</button>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ paddingTop: 70 }}>
      <div className="page-header">
        <h1>MAKE A DEPOSIT</h1>
        <p>Fund your gaming account quickly and securely</p>
      </div>

      <div className="container" style={{ paddingBottom: 80, maxWidth: 720 }}>
        {/* My Deposit History toggle */}
        {myDeposits.length > 0 && (
          <div style={{ marginBottom: 28 }}>
            <button onClick={() => setShowHistory(!showHistory)} className="btn btn-secondary btn-sm">
              {showHistory ? '▲ Hide' : '▼ Show'} My Deposit History ({myDeposits.length})
            </button>
            {showHistory && (
              <div style={{ marginTop: 14 }}>
                <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border)' }}>
                        {['Date','Game','Username','Amount','Payment','Tag','Status'].map(h => (
                          <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, color: 'var(--text-secondary)', fontFamily: 'var(--font-display)', letterSpacing: '0.06em' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {myDeposits.map(d => (
                        <tr key={d._id} style={{ borderBottom: '1px solid var(--border)' }}>
                          <td style={{ padding: '10px 14px', fontSize: 12, color: 'var(--text-muted)' }}>{new Date(d.createdAt).toLocaleDateString()}</td>
                          <td style={{ padding: '10px 14px', fontSize: 13, fontWeight: 600 }}>{d.platform?.name}</td>
                          <td style={{ padding: '10px 14px', fontSize: 13 }}>{d.gameUsername}</td>
                          <td style={{ padding: '10px 14px', fontSize: 14, fontWeight: 700, color: '#10b981' }}>${d.amount}</td>
                          <td style={{ padding: '10px 14px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <PaymentLogo type={d.paymentMethod?.type} size={20} />
                              <span style={{ fontSize: 12 }}>{d.paymentMethod?.name}</span>
                            </div>
                          </td>
                          <td style={{ padding: '10px 14px', fontSize: 12, color: '#f5c400' }}>{d.paymentMethod?.tag || '—'}</td>
                          <td style={{ padding: '10px 14px' }}>
                            <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700,
                              background: d.status==='verified'?'rgba(16,185,129,0.15)':d.status==='rejected'?'rgba(239,68,68,0.12)':'rgba(245,196,0,0.12)',
                              color: d.status==='verified'?'#10b981':d.status==='rejected'?'#ef4444':'#f5c400' }}>
                              {d.status==='verified'?'✅ Verified':d.status==='rejected'?'❌ Rejected':'⏳ Pending'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step indicator */}
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 32 }}>
          {STEPS.map((label, i) => (
            <React.Fragment key={i}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', background: i<step?'#10b981':i===step?'var(--gradient-primary)':'var(--bg-secondary)', border:`2px solid ${i<=step?'#f5c400':'var(--border)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14, color: i<=step?'#0a0900':'var(--text-muted)', transition: 'all 0.3s' }}>
                  {i<step?'✓':i+1}
                </div>
                <span style={{ fontSize: 11, color: i===step?'#f5c400':'var(--text-muted)', fontWeight: i===step?700:400, whiteSpace: 'nowrap' }}>{label}</span>
              </div>
              {i<STEPS.length-1&&<div style={{ flex:1, height:2, background:i<step?'#f5c400':'var(--border)', margin:'0 8px', marginBottom:20, transition:'background 0.3s' }}/>}
            </React.Fragment>
          ))}
        </div>

        <div className="card" style={{ padding: 32 }}>
          {error && <div style={{ background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.3)', borderRadius:8, padding:'10px 14px', color:'#ef4444', marginBottom:20, fontSize:14 }}>{error}</div>}

          {step===0&&(
            <div>
              <h3 style={{ fontFamily:'var(--font-display)', fontSize:18, marginBottom:6, color:'var(--accent-cyan)' }}>SELECT GAME PLATFORM</h3>
              <p style={{ color:'var(--text-secondary)', fontSize:14, marginBottom:20 }}>Which game do you want to fund?</p>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                {platforms.map(p=>(
                  <div key={p._id} onClick={()=>setForm({...form,platform:p._id})} style={{ padding:'14px 16px', borderRadius:10, border:`2px solid ${form.platform===p._id?'#f5c400':'var(--border)'}`, cursor:'pointer', background:form.platform===p._id?'rgba(245,196,0,0.08)':'var(--bg-secondary)', display:'flex', alignItems:'center', gap:12, transition:'all 0.15s' }}>
                    {p.logo?<img src={p.logo} alt={p.name} style={{ width:40,height:40,borderRadius:8,objectFit:'contain',background:'#fff',padding:3 }}/>:<div style={{ width:40,height:40,borderRadius:8,background:'var(--gradient-primary)',display:'flex',alignItems:'center',justifyContent:'center',fontWeight:900,fontSize:16,color:'#0a0900',flexShrink:0 }}>{p.name[0]}</div>}
                    <span style={{ fontWeight:600, fontSize:14 }}>{p.name}</span>
                    {form.platform===p._id&&<span style={{ marginLeft:'auto', color:'#f5c400', fontSize:18 }}>✓</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {step===1&&(
            <div>
              <h3 style={{ fontFamily:'var(--font-display)', fontSize:18, marginBottom:6, color:'var(--accent-cyan)' }}>YOUR DETAILS</h3>
              <div className="form-group"><label>Full Name *</label><input value={form.fullName} onChange={e=>setForm({...form,fullName:e.target.value})} placeholder="Your real name" required/></div>
              <div className="form-group"><label>Game Username *</label><input value={form.gameUsername} onChange={e=>setForm({...form,gameUsername:e.target.value})} placeholder="Your in-game username" required/></div>
              <div className="form-group"><label>Deposit Amount (USD) *</label><input type="number" min="1" value={form.amount} onChange={e=>setForm({...form,amount:e.target.value})} placeholder="e.g. 50" required/></div>
            </div>
          )}

          {step===2&&(
            <div>
              <h3 style={{ fontFamily:'var(--font-display)', fontSize:18, marginBottom:6, color:'var(--accent-cyan)' }}>SELECT PAYMENT METHOD</h3>
              <p style={{ color:'var(--text-secondary)', fontSize:14, marginBottom:20 }}>Choose how you want to send the money</p>
              {paymentMethods.length===0?<div style={{ textAlign:'center',padding:40,color:'var(--text-muted)' }}>No payment methods available. Contact support.</div>:(
                <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                  {paymentMethods.map(pm=>{
                    const isSelected=form.paymentMethod===pm._id;
                    return(
                      <div key={pm._id} onClick={()=>{setForm({...form,paymentMethod:pm._id});setSelectedPayment(pm);}} style={{ padding:'16px 20px', borderRadius:12, border:`2px solid ${isSelected?'#f5c400':'var(--border)'}`, cursor:'pointer', background:isSelected?'rgba(245,196,0,0.06)':'var(--bg-secondary)', display:'flex', alignItems:'center', gap:16, transition:'all 0.15s' }}>
                        <PaymentLogo type={pm.type} size={48}/>
                        <div style={{ flex:1 }}>
                          <div style={{ fontWeight:700, fontSize:16 }}>{pm.name||LABELS[pm.type]}</div>
                          {pm.tag&&<div style={{ fontSize:13, color:'var(--text-secondary)', marginTop:2 }}>Tag: <span style={{ color:'#f5c400', fontWeight:600 }}>{pm.tag}</span></div>}
                          {pm.address&&<div style={{ fontSize:12, color:'var(--text-muted)', fontFamily:'monospace', marginTop:2 }}>{pm.address.slice(0,28)}...</div>}
                        </div>
                        {isSelected&&<span style={{ color:'#f5c400', fontSize:22 }}>✓</span>}
                      </div>
                    );
                  })}
                </div>
              )}
              {selectedPayment&&form.paymentMethod&&(
                <div style={{ marginTop:20, background:'var(--bg-secondary)', border:'1px solid var(--border-glow)', borderRadius:14, padding:20 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:16 }}>
                    <PaymentLogo type={selectedPayment.type} size={36}/>
                    <div>
                      <div style={{ fontFamily:'var(--font-display)', fontSize:13, color:'#f5c400' }}>SEND ${form.amount||'...'} TO:</div>
                      <div style={{ fontWeight:700, fontSize:16 }}>{selectedPayment.name||LABELS[selectedPayment.type]}</div>
                    </div>
                  </div>
                  <div style={{ display:'flex', gap:20, flexWrap:'wrap' }}>
                    {selectedPayment.qrCode&&<div style={{ textAlign:'center' }}><img src={selectedPayment.qrCode} alt="QR" style={{ width:130,height:130,borderRadius:10,border:'2px solid var(--border)',objectFit:'contain',background:'#fff',padding:6 }}/><div style={{ fontSize:11,color:'var(--text-muted)',marginTop:4 }}>Scan QR</div></div>}
                    <div style={{ flex:1 }}>
                      {selectedPayment.tag&&<div style={{ marginBottom:10, background:'var(--bg-card)', borderRadius:8, padding:'10px 14px' }}><div style={{ fontSize:11,color:'var(--text-muted)',marginBottom:3 }}>TAG / USERNAME</div><div style={{ fontSize:20,fontWeight:900,color:'#f5c400',fontFamily:'var(--font-display)' }}>{selectedPayment.tag}</div></div>}
                      {selectedPayment.address&&<div style={{ marginBottom:10, background:'var(--bg-card)', borderRadius:8, padding:'10px 14px' }}><div style={{ fontSize:11,color:'var(--text-muted)',marginBottom:3 }}>ADDRESS</div><div style={{ fontSize:12,fontFamily:'monospace',wordBreak:'break-all' }}>{selectedPayment.address}</div></div>}
                      {selectedPayment.instructions&&<div style={{ fontSize:13,color:'var(--text-secondary)',lineHeight:1.6 }}>ℹ️ {selectedPayment.instructions}</div>}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {step===3&&(
            <div>
              <h3 style={{ fontFamily:'var(--font-display)', fontSize:18, marginBottom:6, color:'var(--accent-cyan)' }}>UPLOAD PAYMENT PROOF</h3>
              <p style={{ color:'var(--text-secondary)', fontSize:14, marginBottom:20 }}>Take a screenshot of your payment confirmation</p>
              <div style={{ background:'var(--bg-secondary)', borderRadius:12, padding:16, marginBottom:20 }}>
                <div style={{ fontSize:12,color:'var(--text-muted)',marginBottom:10,fontFamily:'var(--font-display)',letterSpacing:'0.06em' }}>DEPOSIT SUMMARY</div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                  {[['Game',platforms.find(p=>p._id===form.platform)?.name],['Username',form.gameUsername],['Amount',`$${form.amount}`],['Name',form.fullName]].map(([k,v])=>(
                    <div key={k} style={{ background:'var(--bg-card)',borderRadius:8,padding:'8px 12px' }}><div style={{ fontSize:11,color:'var(--text-muted)',marginBottom:2 }}>{k}</div><div style={{ fontWeight:700,fontSize:14,color:'#f5c400' }}>{v}</div></div>
                  ))}
                </div>
                {selectedPayment&&<div style={{ marginTop:10,display:'flex',alignItems:'center',gap:10,background:'var(--bg-card)',borderRadius:8,padding:'10px 14px' }}><PaymentLogo type={selectedPayment.type} size={26}/><div><div style={{ fontSize:11,color:'var(--text-muted)' }}>Payment via</div><div style={{ fontWeight:700,fontSize:14 }}>{selectedPayment.name||LABELS[selectedPayment.type]}{selectedPayment.tag?` — ${selectedPayment.tag}`:''}</div></div></div>}
              </div>
              <div style={{ border:'2px dashed var(--border-glow)',borderRadius:12,padding:32,textAlign:'center',cursor:'pointer',background:screenshotPreview?'transparent':'var(--bg-secondary)' }} onClick={()=>fileRef.current?.click()} onDragOver={e=>e.preventDefault()} onDrop={e=>{e.preventDefault();const f=e.dataTransfer.files[0];if(f){setScreenshot(f);setScreenshotPreview(URL.createObjectURL(f));}}}>
                {screenshotPreview?(<div><img src={screenshotPreview} alt="screenshot" style={{ maxWidth:'100%',maxHeight:260,borderRadius:10,objectFit:'contain' }}/><p style={{ color:'var(--accent-cyan)',fontSize:13,marginTop:10 }}>Click to change</p></div>):(<><div style={{ fontSize:48,marginBottom:12 }}>📸</div><p style={{ color:'var(--text-primary)',fontWeight:600,marginBottom:6 }}>Drop screenshot here or click to upload</p><p style={{ color:'var(--text-muted)',fontSize:13 }}>PNG, JPG · Max 10MB</p></>)}
              </div>
              <input type="file" ref={fileRef} accept="image/*" onChange={handleScreenshot} style={{ display:'none' }}/>
            </div>
          )}

          <div style={{ display:'flex', gap:12, marginTop:28 }}>
            {step>0&&<button className="btn btn-secondary" onClick={()=>setStep(s=>s-1)} style={{ flex:1 }}>← Back</button>}
            {step<STEPS.length-1?(
              <button className="btn btn-primary" onClick={()=>setStep(s=>s+1)} disabled={!canNext()} style={{ flex:2, opacity:canNext()?1:0.5 }}>Continue →</button>
            ):(
              <button className="btn btn-primary" onClick={handleSubmit} disabled={submitting||!screenshot} style={{ flex:2 }}>{submitting?'⏳ Submitting...':'✅ Submit Deposit'}</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
